/* eslint-disable */
import { Badge, HStack, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEndDate,
  selectList,
  selectLoading,
  selectOrderBy,
  selectOrderDir,
  selectPageNo,
  selectPageSize,
  selectSearch,
  selectStartDate,
  selectStatusFilter,
  selectTotalRow,
} from "./redux/selector";

import { EditIcon } from "@chakra-ui/icons";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { MdReceiptLong } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DateFormat from "../../../components/Common/DateFormat";
import { CommonTable } from "../../../components/CommonTable";
import { apiPath, IS_RETAILER } from "../../../utils/Api";
import { DataImportantStatusEnum } from "../user/types";
import {
  paymentMethodColorMap,
  paymentMethodLabelMap,
  statusColorMap,
  StatusData,
  statusLabelMap,
} from "./data";
import Filter from "./filter";
import { actions } from "./redux/slice";
import { IData, OrderStatusType } from "./types";

interface Props {
  hideFilter?: boolean;
}

export default function Index({ hideFilter }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pageNo = useSelector(selectPageNo);
  const pageSize = useSelector(selectPageSize);
  const search = useSelector(selectSearch);
  const tableData = useSelector(selectList);
  const loading = useSelector(selectLoading);
  const total = useSelector(selectTotalRow);
  const orderBy = useSelector(selectOrderBy);
  const orderDir = useSelector(selectOrderDir);
  const startDate = useSelector(selectStartDate);
  const endDate = useSelector(selectEndDate);
  const statusFilter = useSelector(selectStatusFilter);

  React.useEffect(() => {
    dispatch(actions.doGetList());
    return () => {};
  }, [pageNo, pageSize, search, orderDir, orderBy, statusFilter]);

  const columnHelper = createColumnHelper<IData>();
  const columns = [
    columnHelper.accessor("orderId", {
      id: "orderId",
      header: `${t("order.list.orderId")}`,
      cell: (info) => <Text sx={{minWidth:'80px'}}>{info.getValue() || "NA"}</Text>,
    }),
    columnHelper.accessor("name", {
      id: "name",
      header: `${t("order.list.fullName")}`,
      cell: (info) => <Text sx={{minWidth:150}}>{info.getValue() || "NA"}</Text>,
    }),
    columnHelper.accessor("nameJp", {
      id: "nameJp",
      header: `${t("order.list.fullNameJp")}`,
      cell: (info) => (
        <div style={{ display: "flex", alignItems: "center" ,minWidth:150}}>
          {info.row.original.importantStatus ===
            DataImportantStatusEnum.IMPORTANT && (
            <Badge
              colorScheme={
                info.row.original.importantStatus ===
                DataImportantStatusEnum.IMPORTANT
                  ? "red"
                  : ""
              }
              color={
                info.row.original.importantStatus ===
                DataImportantStatusEnum.IMPORTANT
                  ? "red"
                  : ""
              }
              fontSize="md"
              fontWeight="500"
              sx={{ textTransform: "capitalCase" }}
            >
              &nbsp;
            </Badge>
          )}
          <Text>&nbsp;{info.getValue() || "NA"}</Text>
        </div>
      ),
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: `${t("order.list.email")}`,
      cell: (info) => <Text sx={{minWidth:150}}>{info.getValue() || "NA"}</Text>,
    }),
    columnHelper.accessor("mobile", {
      id: "mobile",
      header: `${t("order.list.phoneNumber")}`,
      cell: (info) => <Text sx={{minWidth:150}}>{info.getValue() || "NA"}</Text>,
    }),
    columnHelper.accessor("paymentMethod", {
      id: "paymentMethod",
      header: `${t("common.list.paymentMethod")}`,
      cell: (info) => {
        const statusValue = info.getValue();
        const label =  t(paymentMethodLabelMap[statusValue]) || "Unknown";
        const color = paymentMethodColorMap[statusValue] || "gray";

        return (
          <Badge
            colorScheme={color}
            fontSize="md"
            fontWeight="500"
            sx={{ textTransform: "capitalize" }}
          >
            {label}
          </Badge>
        );
      },
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: `${t("common.list.status")}`,
      cell: (info) => {
        const statusValue = info.getValue();
        const label = t(statusLabelMap[statusValue]) || "Unknown";
        const color = statusColorMap[statusValue] || "gray";

        return (
          <Badge
            colorScheme={color}
            fontSize="md"
            fontWeight="500"
            sx={{ textTransform: "capitalize" }}
          >
            {label}
          </Badge>
        );
      },
    }),

    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: `${t("common.list.createdAt")}`,
      cell: (info) => <Text>{DateFormat(info.getValue()) || "NA"}</Text>,
      sortDescFirst: true,
    }),
    columnHelper.accessor("updatedAt", {
      id: "updatedAt",
      header: `${t("common.list.updatedAt")}`,
      cell: (info) => <Text>{DateFormat(info.getValue()) || "NA"}</Text>,
    }),

    columnHelper.accessor("_id", {
      id: "_id",
      header: `${t("common.list.actions")}`,
      cell: (info) => {
        return (
          <HStack>
            <Tooltip label={`${t("common.list.edit")}`}>
              <IconButton
                aria-label="Edit"
                icon={<EditIcon />}
                onClick={() => {
                  navigate(`/admin/order/create/${info.row.original._id}`);
                }}
              />
            </Tooltip>
            <Tooltip label={t("common.list.invoice")}>
              <IconButton
                aria-label="Export CSV"
                icon={<MdReceiptLong size={20} />}
                onClick={() => {
                  handleDownload(info.row.original.invoiceId);
                }}
                colorScheme="#fff"
                variant="unstyled"
                size="md"
              />
            </Tooltip>
            {IS_RETAILER === "true" && (
              <Tooltip label={t("common.list.retailerInvoice")}>
                <IconButton
                  aria-label="Export CSV"
                  icon={<MdReceiptLong size={20} />}
                  onClick={() => {
                    handleRetailerInvoiceDownload(info.row.original.invoiceId);
                  }}
                  colorScheme="#fff"
                  variant="unstyled"
                  size="md"
                />
              </Tooltip>
            )}
          </HStack>
        );
      },
    }),
  ];

  const handleDate = () => {
    if (startDate === null) {
      toast.error(t("order.list.enterStartDate"));
      return;
    }
    if (endDate === null) {
      toast.error(t("order.list.enterEndDate"));
      return;
    }
    const start =
      typeof startDate === "string" ? DateTime.fromISO(startDate) : startDate;
    const end =
      typeof endDate === "string" ? DateTime.fromISO(endDate) : endDate;

    if (end < start) {
      toast.error(t("order.list.validEndDate"));
      return;
    }
    dispatch(actions.doGetList());
  };
  const onClearDate = () => {
    dispatch(actions.setEndDate(null));
    dispatch(actions.setStartDate(null));
    // dispatch(actions.doGetList());
  };

 const handleEndDate = (date: DateTime | null) => {
    //@ts-ignore
    dispatch(actions.setEndDate(date?.toFormat("yyyy-MM-dd")));
  };
  const handleStartDate = (date: DateTime | null) => {
    //@ts-ignore
    dispatch(actions.setStartDate(date?.toFormat("yyyy-MM-dd")));
  };

  const handleStatusChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    dispatch(actions.setStatusFilter(value as unknown as OrderStatusType));
  };
  const handleDownload = (id: string) => {
    window.open(`${apiPath()}user/order/invoice/${id}`);
  };
  const handleRetailerInvoiceDownload = (id: string) => {
    window.open(`${apiPath()}retailer/order/invoice/${id}`);
  };
  return (
    <>
      <CommonTable<IData>
        columns={columns}
        data={tableData}
        totalCount={total}
        pageNo={pageNo}
        pageSize={pageSize}
        search={search}
        loading={loading}
        onSearch={(v) => dispatch(actions.setSearch(v))}
        onPageChange={(p) => dispatch(actions.setPageNo(p))}
        onPageSizeChange={(s) => dispatch(actions.setPageSize(s))}
        onSortChange={(id, desc) => {
          dispatch(actions.setOrderBy(id));
          dispatch(actions.setOrderDir(desc ? "desc" : "asc"));
        }}
        hideAddButton
        showSearch={hideFilter}
        showPagination={hideFilter}
        filters={
          !hideFilter && (
            <Filter
              endDate={endDate}
              startDate={startDate}
              handleEndDate={handleEndDate}
              handleStartDate={handleStartDate}
              isLoading={false}
              onClear={onClearDate}
              StatusData={StatusData}
              onSearchDate={handleDate}
              handleStatusChange={handleStatusChange}
              statusValue={statusFilter}
            />
          )
        }
      />
    </>
  );
}
